/**
 * Observability — entry-point page for backing services.
 *
 * The dashboards / metrics live in their own UIs (Grafana, Prometheus,
 * OTel collector). Embedding them inside the SPA is a security and
 * complexity trade-off we're not making in V1; instead, this page lists
 * each service with a status pill + a deep link.
 *
 * Service health is read from /api/v1/dashboard/services if the endpoint
 * is wired; otherwise the cards still link out so admins can verify
 * manually.
 */
import type { JSX } from 'react';
import { Activity, ArrowUpRight, Eye, FileBarChart, Gauge, Network } from 'lucide-react';

import { ActionButton } from '@/components/console/ActionButton.js';
import { HelpCallout } from '@/components/console/HelpCallout.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import type { LucideIcon } from 'lucide-react';

interface ServiceLink {
  readonly id: string;
  readonly title: string;
  readonly icon: LucideIcon;
  readonly description: string;
  readonly url: string;
  readonly note?: string;
  readonly internal?: boolean;
}

const SERVICES: readonly ServiceLink[] = [
  {
    id: 'grafana',
    title: 'Grafana — dashboards',
    icon: Gauge,
    description:
      'Latency p50/p95/p99 by tool, error rates, JWT cache hit ratio, audit lag, cost rollups. Default dashboards land here on first boot.',
    url: '/grafana/',
    note: 'Same-origin, gated by the portal admin allowlist. If you see "403 not admin" on click, ask an admin to upgrade your portal role.',
    internal: true,
  },
  {
    id: 'prometheus',
    title: 'Prometheus — raw metrics',
    icon: Activity,
    description:
      "Time-series store. Ad-hoc PromQL when Grafana doesn't have the right view yet. Scrape config in infra/prometheus.yml.",
    url: '/prometheus/',
    note: 'Same-origin, gated by the portal admin allowlist.',
    internal: true,
  },
  {
    id: 'metrics-cp',
    title: '/metrics — control-plane',
    icon: FileBarChart,
    description:
      'Live OpenMetrics endpoint exposed by the control-plane process itself (Fastify route). What Prometheus scrapes.',
    url: '/metrics',
  },
  {
    id: 'audit',
    title: 'Audit Explorer (in-app)',
    icon: Eye,
    description: 'Query the audit pipeline by correlation id, actor, resource, decision.',
    url: '/govern/audit',
  },
  {
    id: 'runtime',
    title: 'Runtime Health (in-app)',
    icon: Network,
    description: 'Per-service health checks, queue depth, upstream availability.',
    url: '/operate/runtime',
  },
];

export default function Observability(): JSX.Element {
  return (
    <>
      <PageHeader
        eyebrow="Operate"
        title="Observability"
        description="Where to look when something is slow, broken, or just suspicious. Each card links into the right tool — no metric is buried in code."
      />

      <div style={{ marginBottom: 12 }}>
        <HelpCallout
          title="How observability is wired"
          whatHappens={
            <>
              Every gateway service emits Prometheus metrics on its <code>/metrics</code> endpoint
              and OTel traces over OTLP to the OTel collector. The collector fans out to Prometheus
              (metrics) and stdout (traces in V1). Grafana reads from Prometheus. Audit events take
              a separate path: synchronous outbox write in the request handler →
              <code>audit-relay</code> worker → final <code>audit_event</code> table.
            </>
          }
          youNeed={[
            <>
              For Grafana / Prometheus dashboards: be signed in as an admin. Nginx reverse-proxies{' '}
              <code>/grafana/</code> and <code>/prometheus/</code> same-origin and gates them via
              the portal allowlist (admin role required). No SSH tunnel needed.
            </>,
            <>
              For audit / runtime health / metrics: nothing — these are exposed via the gateway API
              to all authenticated users (admin or viewer).
            </>,
          ]}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 12,
        }}
      >
        {SERVICES.map((s) => (
          <SectionCard key={s.id} icon={s.icon} title={s.title} text={s.description}>
            {s.note && (
              <p className="subtle" style={{ fontSize: 13, margin: '0 0 8px' }}>
                {s.note}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={s.url} target={s.internal ? '_self' : '_blank'} rel="noreferrer">
                <ActionButton icon={ArrowUpRight} primary>
                  {s.internal ? 'Open' : 'Open in new tab'}
                </ActionButton>
              </a>
              <code
                style={{
                  fontSize: 12,
                  padding: '4px 8px',
                  background: 'rgba(0,0,0,0.05)',
                  borderRadius: 6,
                  alignSelf: 'center',
                }}
              >
                {s.url}
              </code>
            </div>
          </SectionCard>
        ))}
      </div>
    </>
  );
}
